/**
 * @file CompaniesPage.tsx
 * @brief Super Admin → Companies page. Mirrors the Users-page architecture
 *        (URL-synced filters, server-paginated table, dialog-driven CRUD).
 *
 * @details
 * - URL query params drive filter state so reloads / shareable links
 *   preserve the view.
 * - Mutations invalidate the list query → table refreshes automatically.
 * - Per-currency revenue is summarised via `formatMoneyMinor` (defaults
 *   to GBP). When custom rates exist on a row we surface them as a small
 *   muted hint under the company name.
 *
 * @dependencies
 * - features/companies/api (TanStack Query hooks)
 * - features/companies/components/* (badges, dialogs, filters, row actions)
 * - components/data/DataTable
 *
 * @references
 * - BACKEND/src/features/admin/routes.js (companies endpoints)
 *
 * @author DES
 * @date 26-05-2026
 * @updated 29-07-2026 — GridBlock end-to-end (match Users)
 */

import { DockActionButton, PageDock, pageWithDockClass } from "@/components/ui/PageDock";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Building2, Plus } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/Button";
import { useOpenCreateFromQuery } from "@/shared/hooks/useOpenCreateFromQuery";
import {
  DataTable,
  type PaginationState,
} from "@/components/data/DataTable";
import type { TableRowGroup } from "@/components/data/groupRowsBy";
import { formatMoneyMinor, formatRelative } from "@/shared/utils/formatters";
import { statusGroupMeta } from "@/shared/utils/tableGroups";
import {
  GridBand,
  GridBlock,
  GridCell,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import {
  companiesStatusGroupKey,
  useCompaniesGroupRows,
  useCompaniesGroupSummary,
  useCompaniesList,
} from "./api";
import type { AdminCompany, AdminCompaniesListParams } from "./schemas";
import {
  CompaniesFilters,
  CompaniesHeaderFilters,
} from "./components/CompaniesFilters";
import { CompanyRowActions } from "./components/CompanyRowActions";
import { CompanyStatusBadge } from "./components/CompanyStatusBadge";
import { CompanyPlanBadge } from "./components/CompanyPlanBadge";
import { CreateCompanyDialog } from "./components/CreateCompanyDialog";
import { EditCompanyDialog } from "./components/EditCompanyDialog";

// ----------------------------------------------------------------------
// URL <-> filter-state helpers
// ----------------------------------------------------------------------
function paramsFromSearch(search: URLSearchParams): AdminCompaniesListParams {
  const status = search.get("status");
  return {
    page: Number(search.get("page")) || 1,
    limit: Number(search.get("limit")) || 25,
    search: search.get("q") || undefined,
    status:
      status === "active" || status === "suspended" || status === "deleted"
        ? status
        : undefined,
  };
}

function searchFromParams(p: AdminCompaniesListParams): URLSearchParams {
  const usp = new URLSearchParams();
  if (p.page && p.page !== 1) usp.set("page", String(p.page));
  if (p.limit && p.limit !== 25) usp.set("limit", String(p.limit));
  if (p.search) usp.set("q", p.search);
  if (p.status) usp.set("status", p.status);
  return usp;
}

// ----------------------------------------------------------------------
// Tiny inline summary of any custom rate overrides on a company row.
// ----------------------------------------------------------------------
function customRatesSummary(c: AdminCompany): string | null {
  const parts: string[] = [];
  if (c.custom_rate_gbp_minor) {
    parts.push(`${formatMoneyMinor(c.custom_rate_gbp_minor, "GBP")} (GBP)`);
  }
  if (c.custom_rate_eur_minor) {
    parts.push(`${formatMoneyMinor(c.custom_rate_eur_minor, "EUR", "en-IE")} (EUR)`);
  }
  if (c.custom_rate_usd_minor) {
    parts.push(`${formatMoneyMinor(c.custom_rate_usd_minor, "USD", "en-US")} (USD)`);
  }
  return parts.length > 0 ? `Custom: ${parts.join(" · ")}` : null;
}

// ----------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------
export function CompaniesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => paramsFromSearch(searchParams), [searchParams]);

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AdminCompany | null>(null);

  // Cmd+K “Create company” → /admin/companies?create=1 (17-07-2026)
  useOpenCreateFromQuery(setCreateOpen);

  const isGrouped = !params.status;
  /**
   * Grouped (no status picked): every status from the server with its
   * company count, not just the companies loaded so far (DES 01-10-2026:
   * "haan superadmin pages pe bhi yehi kar do"), as on company Invoices. A
   * group fetches its own companies when opened, 25 at a time. No stat cards
   * here, so the flat list is off while grouped.
   */
  const list = useCompaniesList(params, { enabled: !isGrouped });
  const summary = useCompaniesGroupSummary(params, isGrouped);
  // Open groups live in the URL (`open=key:batches,...`).
  const openParam = searchParams.get("open") ?? "";
  const openGroups = useMemo(() => {
    const map = new Map<string, number>();
    for (const part of openParam.split(",")) {
      const [key, n] = part.split(":");
      if (key) map.set(key, Math.max(1, Number(n) || 1));
    }
    return map;
  }, [openParam]);
  const setOpenGroups = (update: (prev: Map<string, number>) => Map<string, number>) => {
    const next = update(openGroups);
    const usp = new URLSearchParams(searchParams);
    const value = [...next].map(([k, n]) => (n > 1 ? `${k}:${n}` : k)).join(",");
    if (value) usp.set("open", value);
    else usp.delete("open");
    setSearchParams(usp, { replace: true });
  };
  // A search opens every group, so matches are visible.
  const searching = isGrouped && Boolean(params.search?.trim());
  const openList = useMemo(() => {
    const keys = searching
      ? (summary.data ?? []).map((g) => companiesStatusGroupKey(g.status))
      : [...openGroups.keys()];
    return keys.map((key) => ({ key, batches: openGroups.get(key) ?? 1 }));
  }, [searching, summary.data, openGroups]);
  const groupRows = useCompaniesGroupRows(params, openList);
  const groups = useMemo<TableRowGroup<AdminCompany>[] | undefined>(() => {
    if (!isGrouped || !summary.data?.length) return undefined;
    return summary.data
      .map((g) => {
        const key = companiesStatusGroupKey(g.status);
        const idx = openList.findIndex((o) => o.key === key);
        const q = idx >= 0 ? groupRows[idx] : undefined;
        return {
          key,
          ...statusGroupMeta(g.status ?? ""),
          rows: q?.data?.companies ?? [],
          total: g.count,
          loading: Boolean(q?.isFetching),
          error: Boolean(q?.isError),
          onRetry: () => void q?.refetch(),
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [isGrouped, summary.data, openList, groupRows]);
  const rows = isGrouped
    ? (groups ?? []).flatMap((g) => g.rows)
    : list.data?.companies ?? [];
  const pagination: PaginationState | undefined =
    !isGrouped && list.data?.pagination
      ? {
          page: list.data.pagination.page,
          limit: list.data.pagination.limit,
          total: list.data.pagination.total,
          totalPages: list.data.pagination.totalPages,
        }
      : undefined;

  const setParams = (next: AdminCompaniesListParams) =>
    setSearchParams(searchFromParams(next), { replace: false });

  const columns = useMemo<ColumnDef<AdminCompany>[]>(
    () => [
      {
        id: "company",
        header: "Company",
        cell: ({ row }) => {
          const c = row.original;
          const ratesHint = customRatesSummary(c);
          return (
            <div className="flex items-center gap-3">
              <div className="flex h-9 shrink-0 items-center text-brand">
                <Building2 className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{c.name}</p>
                {c.domain ? (
                  <p className="truncate text-xs text-muted-foreground">
                    {c.domain}
                  </p>
                ) : ratesHint ? (
                  <p className="truncate text-xs text-muted-foreground">
                    {ratesHint}
                  </p>
                ) : null}
              </div>
            </div>
          );
        },
      },
      ...(isGrouped
        ? []
        : [
            {
              id: "status",
              header: "Status",
              cell: ({ row }) => (
                <CompanyStatusBadge status={row.original.status} />
              ),
            } satisfies ColumnDef<AdminCompany>,
          ]),
      {
        id: "users",
        header: "Employees",
        cell: ({ row }) => (
          <span className="tabular-nums text-sm">
            {Number(row.original.user_count ?? 0).toLocaleString()}
          </span>
        ),
      },
      {
        id: "sessions",
        header: "Sessions",
        cell: ({ row }) => (
          <span className="tabular-nums text-sm">
            {Number(row.original.session_count ?? 0).toLocaleString()}
          </span>
        ),
      },
      {
        id: "revenue",
        header: "Revenue (GBP)",
        cell: ({ row }) => (
          <span className="tabular-nums text-sm font-medium">
            {formatMoneyMinor(row.original.total_revenue_cents, "GBP")}
          </span>
        ),
      },
      {
        id: "plan",
        header: "Plan / minutes",
        cell: ({ row }) => <CompanyPlanBadge company={row.original} />,
      },
      {
        id: "billing",
        header: "Billing email",
        cell: ({ row }) => {
          const email = row.original.billing_email;
          return email ? (
            <span className="truncate text-sm">{email}</span>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          );
        },
      },
      {
        id: "created_at",
        header: "Created",
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {formatRelative(row.original.created_at)}
          </span>
        ),
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <CompanyRowActions
              company={row.original}
              onEdit={(c) => setEditTarget(c)}
            />
          </div>
        ),
      },
    ],
    [isGrouped],
  );

  return (
    <div className={gridPageBleedClass(`pb-6 ${pageWithDockClass}`)}>
      <GridBlock>
        <GridBand className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1 basis-72">
            <h2 className="text-xl font-semibold tracking-tight">Companies</h2>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              Tenant companies — onboard, edit billing, suspend, or delete.
              Status changes cascade to every employee.
            </p>
          </div>
          {/* The filter, top right (DES 01-10-2026): a text pill, then the
              action. */}
          <div className="flex flex-wrap items-center justify-end gap-2.5">
            <CompaniesHeaderFilters value={params} onChange={setParams} />
          </div>
        </GridBand>

        <GridCell padded={false} className="border-b-0">
          <DataTable<AdminCompany, unknown>
            variant="cell"
            columns={columns}
            data={rows}
            groups={groups}
            expandAllGroups={searching}
            expandedGroupKeys={isGrouped ? [...openGroups.keys()] : undefined}
            onGroupExpandChange={(key, expanded) =>
              setOpenGroups((prev) => {
                const next = new Map(prev);
                if (expanded) next.set(key, next.get(key) ?? 1);
                else next.delete(key);
                return next;
              })
            }
            onGroupLoadMore={(key) =>
              setOpenGroups((prev) => new Map(prev).set(key, (prev.get(key) ?? 1) + 1))
            }
            groupItemLabel="companies"
            isLoading={isGrouped ? summary.isLoading : list.isLoading}
            isFetching={isGrouped ? summary.isFetching : list.isFetching}
            error={isGrouped ? summary.error : list.error}
            onRetry={() => (isGrouped ? summary.refetch() : list.refetch())}
            rowKey={(c) => c.id}
            pagination={pagination}
            onPageChange={(page) => setParams({ ...params, page })}
            onLimitChange={(limit) =>
              setParams({ ...params, limit, page: 1 })
            }
            emptyTitle="No companies match those filters"
            emptyDescription="Try clearing filters or onboard the first one below."
            emptyAction={
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCreateOpen(true)}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Create the first company
              </Button>
            }
          />
        </GridCell>
      </GridBlock>

      {/* The search, then the page's action, docked at the bottom like
          Notes (DES 01-10-2026). */}
      <PageDock>
        <CompaniesFilters value={params} onChange={setParams} />
        <DockActionButton icon={<Plus className="h-5 w-5" strokeWidth={2} />} onClick={() => setCreateOpen(true)}>
          New company
        </DockActionButton>
      </PageDock>

      <CreateCompanyDialog open={createOpen} onOpenChange={setCreateOpen} />
      <EditCompanyDialog
        company={editTarget}
        open={editTarget != null}
        onOpenChange={(open) => !open && setEditTarget(null)}
      />
    </div>
  );
}
